import { IPaperSize, PaperSizes } from "../@shared/lib/PaperSizesUtils";
import * as PDFJS from "pdfjs-dist";
import { useState } from "react";
import styles from "./FileInputSelector.module.css";

interface IProps {
  onFileContentUpdated: (fileContent: File) => void;
  onFileUpdated: (numberOfPages: number, paperSize: IPaperSize | null) => void;
  requiresMultiplesOf4Mode: boolean;
  availablePaperSizes: IPaperSize[];
  selectedPaperSize: string;
}

function FileInputSelector({
  onFileContentUpdated,
  onFileUpdated,
  requiresMultiplesOf4Mode,
  availablePaperSizes,
  selectedPaperSize,
}: IProps) {
  const [warning, setWarning] = useState("");
  const [filePaperSize, setFilePaperSize] = useState<IPaperSize | null>(null);

  const paperSizes = [
    {
      paperSize: PaperSizes.filter((ps) => ps.code === "A0")[0] ?? null,
      width: 841,
      height: 1189,
    },
    {
      paperSize: PaperSizes.filter((ps) => ps.code === "A1")[0] ?? null,
      width: 594,
      height: 841,
    },
    {
      paperSize: PaperSizes.filter((ps) => ps.code === "A2")[0] ?? null,
      width: 420,
      height: 594,
    },
    {
      paperSize: PaperSizes.filter((ps) => ps.code === "A3")[0] ?? null,
      width: 297,
      height: 420,
    },
    {
      paperSize: PaperSizes.filter((ps) => ps.code === "A4")[0] ?? null,
      width: 210,
      height: 297,
    },
    {
      paperSize: PaperSizes.filter((ps) => ps.code === "A5")[0] ?? null,
      width: 148,
      height: 210,
    },
    {
      paperSize: {
        code: "Letter",
        name: "Letter",
        availableTo: [],
        multiplesOfFourCode: "",
      },
      width: 215.9,
      height: 279.4,
    },
  ];

  const getStandardPaperSizeWithOrientation = (
    widthCm: number,
    heightCm: number,
  ) => {
    // Convert width and height to inches
    const widthInMM = widthCm * 10;
    const heightInMM = heightCm * 10;

    // Find the paper size that matches the input width and height
    const matchedPaperSize = paperSizes.find((paperSize) => {
      return (
        (Math.abs(paperSize.width - widthInMM) < 1 &&
          Math.abs(paperSize.height - heightInMM) < 1) ||
        (Math.abs(paperSize.height - widthInMM) < 1 &&
          Math.abs(paperSize.width - heightInMM) < 1)
      );
    });
    const aspectRatio = widthInMM / heightInMM;
    const orientation = aspectRatio < 1 ? true : false;

    if (matchedPaperSize) {
      return {
        paperSize: matchedPaperSize.paperSize,
        portraitMode: orientation,
      };
    } else {
      return { paperSize: null, portraitMode: orientation };
    }
  };
  const onDocumentLoadSuccess = async (pdfDocument: PDFJS.PDFDocumentProxy) => {
    let pagesByType = new Map<IPaperSize, number>();

    for (let i = 0; i < pdfDocument.numPages; i++) {
      await pdfDocument.getPage(i + 1).then(function (page) {
        // get page size from page
        let viewport = page.getViewport({ scale: 1 });
        // get viewport in width and height in centimetres
        const pageHeight = (viewport.height / 72) * 2.54;
        const pageWidth = (viewport.width / 72) * 2.54;

        const paperDetails = getStandardPaperSizeWithOrientation(
          pageWidth,
          pageHeight,
        );
        if (paperDetails.paperSize) {
          if (pagesByType.has(paperDetails.paperSize)) {
            pagesByType.set(
              paperDetails.paperSize,
              (pagesByType.get(paperDetails.paperSize) ?? 0) + 1,
            );
          } else {
            pagesByType.set(paperDetails.paperSize, 1);
          }
        }
      });
    }

    if (pagesByType.size === 1) {
      const paperSize = pagesByType.keys().next().value;
      setFilePaperSize(paperSize);

      if (requiresMultiplesOf4Mode) {
        onFileUpdated(
          pdfDocument.numPages,
          PaperSizes.filter(
            (ps) => ps.code === paperSize.multiplesOfFourCode,
          )[0],
        );
      } else {
        onFileUpdated(pdfDocument.numPages, paperSize);
      }
    } else if (pagesByType.size > 1) {
      onFileUpdated(pdfDocument.numPages, null);
      setWarning(
        "Multiple paper sizes detected. Papers with mismatched sizes will be printed in the paper size selected.",
      );
    } else {
      onFileUpdated(pdfDocument.numPages, null);
      setWarning(
        "Unknown paper size detected. Document will be printed in the paper size selected.",
      );
    }
  };

  const handleFileInputChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!event.target.files) return;

    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith(".pdf")) {
      event.target.value = "";
      setWarning("Only PDF files are supported.");
      return;
    }

    if (file.size > 30 * 1024 * 1024) {
      event.target.value = "";
      setWarning("File cannot be larger than 30Mb.");
      return;
    }

    const buffer = await file.arrayBuffer();
    onFileContentUpdated(file);
    PDFJS.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS.version}/pdf.worker.min.mjs`;

    await PDFJS.getDocument(buffer).promise.then(onDocumentLoadSuccess);
  };

  let warningText = "";

  if (requiresMultiplesOf4Mode) {
    if (
      filePaperSize !== null &&
      selectedPaperSize !== "" &&
      filePaperSize.multiplesOfFourCode !== selectedPaperSize
    ) {
      const requiredPaperSize = PaperSizes.filter(
        (p) => p.multiplesOfFourCode === selectedPaperSize,
      )[0];
      warningText =
        "Incorrect paper size (" +
        filePaperSize?.code +
        ") detected in document. The document will be adjusted to " +
        requiredPaperSize.code +
        ".  An " +
        selectedPaperSize +
        " paper size produces an " +
        requiredPaperSize.code +
        " booklet, therefore each page is " +
        requiredPaperSize.code +
        " size.";
    }
  } else {
    const availablePaperSizeFilter = availablePaperSizes.filter(
      (aps) => aps.code === filePaperSize?.code,
    );
    if (filePaperSize !== null && availablePaperSizeFilter.length === 0) {
      warningText =
        filePaperSize.name +
        " paper size detected but not available for printing. Document will be printed in the paper size selected.";
    }

    if (
      warningText === "" &&
      filePaperSize !== null &&
      selectedPaperSize !== "" &&
      filePaperSize.code !== selectedPaperSize
    ) {
      warningText =
        filePaperSize.name +
        " paper size detected but " +
        selectedPaperSize +
        " is selected for printing. Document will be printed in the paper size selected.";
    }
  }
  if (warning !== warningText) {
    setWarning(warningText);
  }

  const warningAlertRow = warning ? (
    <tr>
      <th className="label"></th>
      <td className={"value " + styles.warning}>{warning}</td>
    </tr>
  ) : null;

  return (
    <>
      <tr>
        <th className="label">
          <label htmlFor="cq-pod-file">
            File to Print <span color="red">*</span>
          </label>
          <span
            className="woo-selected-variation-item-name"
            data-default=""
          ></span>
        </th>
        <td className="value">
          <input
            type="file"
            name="cq-pod-file"
            data-testid="cq-pod-file-input"
            accept=".pdf,application/pdf"
            onChange={handleFileInputChange}
          />
        </td>
      </tr>
      {warningAlertRow}
    </>
  );
}

export default FileInputSelector;
